/**
 * Product-page lazy media readiness, text-video playback and *-prev recommendations.
 */
(function () {
  'use strict';

  if (document.documentElement.dataset.productPrevRuntimeBound === 'true') return;
  document.documentElement.dataset.productPrevRuntimeBound = 'true';

  var fadeSelector = '.product-lazy-fade';
  var loadedClass = 'is-product-lazy-loaded';
  var initializedFadeMedia = new WeakSet();
  var productRoot;
  var mediaMutationObserver;
  var modalMediaObservers = new Map();
  var videoObserver;
  var observedVideos = new Set();

  function forEachMatch(root, selector, callback) {
    if (root.matches && root.matches(selector)) callback(root);
    root.querySelectorAll(selector).forEach(callback);
  }

  function fadeTarget(media) {
    if (!media || !media.matches || !productRoot || !productRoot.contains(media) || !media.closest('#main, quick-buy-modal')) return null;
    if (media.matches('img' + fadeSelector)) {
      // Card swatches create eager Images and copy only className. Keep their
      // existing animation; a carousel-promoted original retains this attribute.
      if (media.matches('.product-card__image') && !media.hasAttribute('data-product-lazy-card')) {
        media.classList.remove('product-lazy-fade', loadedClass);
        return null;
      }
      if (media.hasAttribute('data-product-lazy-auto')) {
        if (media.getAttribute('loading') !== 'lazy') return null;
        // Some carousels remove loading=lazy when promoting an adjacent image.
        // Keep the render-time opt-in once native lazy loading is confirmed.
        media.removeAttribute('data-product-lazy-auto');
      }
      return media;
    }
    if (!media.matches('img, video')) return null;
    return media.closest(fadeSelector + '[data-product-lazy-media]');
  }

  function initFadeMedia(media, reset) {
    var target = fadeTarget(media);
    if (!target) return;
    var settled = media.tagName === 'IMG' ? media.complete : media.readyState >= 2 || !!media.error;
    if (settled) {
      target.classList.add(loadedClass);
    } else if (reset || !initializedFadeMedia.has(media)) {
      // A cloned className may belong to the previous resource (card swatches).
      target.classList.remove(loadedClass);
    }
    initializedFadeMedia.add(media);
  }

  function onMediaSettled(event) {
    var media = event.target;
    if (media.matches && media.matches('source')) media = media.closest('video');
    var target = fadeTarget(media);
    if (target) {
      initializedFadeMedia.add(media);
      target.classList.add(loadedClass);
    }
  }

  function playTextVideo(video) {
    if (!video.getAttribute('src') && video.dataset.productVideoSrc) {
      video.setAttribute('src', video.dataset.productVideoSrc);
      video.load();
    }
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
  }

  function observeTextVideo(video) {
    if (!productRoot.contains(video) || observedVideos.has(video)) return;
    observedVideos.add(video);
    if (!('IntersectionObserver' in window)) {
      playTextVideo(video);
      return;
    }
    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!observedVideos.has(entry.target)) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            playTextVideo(entry.target);
          } else {
            entry.target.pause();
          }
        });
      }, { threshold: 0.5 });
    }
    videoObserver.observe(video);
  }

  function initProductMedia(root) {
    if (!productRoot) return;
    if (root.matches && root.matches('img, video')) initFadeMedia(root);
    forEachMatch(root, fadeSelector, function (target) {
      var media = target.matches('img, video') ? target : target.querySelector('img, video');
      if (media) initFadeMedia(media);
    });
    forEachMatch(root, 'video[data-product-lazy-video]', observeTextVideo);
  }

  function releaseProductMedia(root) {
    forEachMatch(root, 'video[data-product-lazy-video]', function (video) {
      if (!observedVideos.delete(video)) return;
      if (videoObserver) videoObserver.unobserve(video);
      video.pause();
    });
    if (videoObserver && observedVideos.size === 0) {
      videoObserver.disconnect();
      videoObserver = null;
    }
  }

  function observeProductMedia(root) {
    var observer = new MutationObserver(function (records) {
      records.forEach(function (record) {
        if (record.type === 'attributes') {
          var media = record.target;
          if (media.matches('source')) {
            media = media.closest('video') || (media.parentElement && media.parentElement.querySelector('img'));
          }
          if (media) initFadeMedia(media, true);
          return;
        }
        record.removedNodes.forEach(function (node) {
          // Ignore moves within the gallery/carousels, but release removed sections.
          if (node.nodeType === 1 && !productRoot.contains(node)) releaseProductMedia(node);
        });
        record.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && productRoot.contains(node)) initProductMedia(node);
        });
      });
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset'],
    });
    return observer;
  }

  function initProductMediaRuntime() {
    productRoot = document.querySelector('body.template-product');
    var main = productRoot && productRoot.querySelector('#main');
    if (!main) return;
    // Capture also reaches Quick Buy after the theme moves its modal to body.
    // Mutation observation stays on #main and individual open Quick Buy modals.
    ['load', 'error', 'loadeddata'].forEach(function (type) {
      productRoot.addEventListener(type, onMediaSettled, true);
    });
    mediaMutationObserver = observeProductMedia(main);
    initProductMedia(main);
  }

  function shopifyRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function initMaupetsSwipers(root) {
    if (!window.MaupetsSwiper || typeof window.MaupetsSwiper.initThemeSwipers !== 'function') {
      return;
    }

    window.requestAnimationFrame(function () {
      window.MaupetsSwiper.initThemeSwipers(root);
    });
  }

  var observedRecommendationSections = new WeakSet();
  var recommendationObserver;

  function getRecommendationObserver() {
    if (recommendationObserver) {
      return recommendationObserver;
    }

    recommendationObserver = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          return;
        }

        observer.unobserve(entry.target);
        loadProductRecommendations(entry.target);
      });
    }, {
      rootMargin: '600px 0px',
      threshold: 0.01,
    });

    return recommendationObserver;
  }

  function initFeaturedCollectionTabsOnce() {
    if (document.documentElement.dataset.prevFeaturedTabsBound === '1') {
      return;
    }
    document.documentElement.dataset.prevFeaturedTabsBound = '1';
    document.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-action="toggle-tab"]');
      if (!tab || !tab.closest('.featured-collections-prev-section')) {
        return;
      }
      var section = tab.closest('.featured-collections-prev-section');
      if (!section) {
        return;
      }
      e.preventDefault();
      if (tab.classList.contains('is-active')) {
        return;
      }
      section.querySelectorAll('.TabList__Item.is-active').forEach(function (t) {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');
      var panelId = tab.getAttribute('aria-controls');
      section.querySelectorAll('.TabPanel[role="tabpanel"]').forEach(function (p) {
        var hidden = p.id !== panelId;
        p.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      });
    });
  }

  function loadProductRecommendations(sectionRoot) {
    if (sectionRoot.dataset.recommendationsLoading === 'true' || sectionRoot.dataset.recommendationsLoaded === 'true') {
      return Promise.resolve();
    }

    sectionRoot.dataset.recommendationsLoading = 'true';
    sectionRoot.setAttribute('aria-busy', 'true');
    var settingsRaw = sectionRoot.getAttribute('data-section-settings');
    if (!settingsRaw) {
      sectionRoot.dataset.recommendationsLoading = 'false';
      sectionRoot.dataset.recommendationsLoaded = 'true';
      sectionRoot.removeAttribute('aria-busy');
      return Promise.resolve();
    }
    var settings;
    try {
      settings = JSON.parse(settingsRaw);
    } catch (err) {
      sectionRoot.dataset.recommendationsLoading = 'false';
      sectionRoot.dataset.recommendationsLoaded = 'true';
      sectionRoot.removeAttribute('aria-busy');
      return Promise.resolve();
    }
    var sectionId = sectionRoot.getAttribute('data-section-id');
    if (!sectionId || settings.productId == null) {
      sectionRoot.dataset.recommendationsLoading = 'false';
      sectionRoot.dataset.recommendationsLoaded = 'true';
      sectionRoot.removeAttribute('aria-busy');
      initMaupetsSwipers(sectionRoot);
      return Promise.resolve();
    }
    var limit = settings.recommendationsCount || 4;
    var url =
      shopifyRoot() +
      'recommendations/products?section_id=' +
      encodeURIComponent(sectionId) +
      '&product_id=' +
      encodeURIComponent(String(settings.productId)) +
      '&limit=' +
      encodeURIComponent(String(limit)) +
      '&intent=related';

    return fetch(url, { priority: 'low' })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.querySelector('.ProductRecommendations');
        var target = sectionRoot.querySelector('.ProductRecommendations');
        if (incoming && target) {
          target.innerHTML = incoming.innerHTML;
          initProductMedia(target);
          initMaupetsSwipers(sectionRoot);
        }
      })
      .catch(function () {})
      .finally(function () {
        sectionRoot.dataset.recommendationsLoading = 'false';
        sectionRoot.dataset.recommendationsLoaded = 'true';
        sectionRoot.removeAttribute('aria-busy');
      });
  }

  function observeProductRecommendations(sectionRoot) {
    if (!sectionRoot || observedRecommendationSections.has(sectionRoot)) {
      return;
    }

    observedRecommendationSections.add(sectionRoot);
    if ((window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
      loadProductRecommendations(sectionRoot);
      return;
    }

    getRecommendationObserver().observe(sectionRoot);
  }

  function initProductRecommendationsSections(root) {
    var scope = root && root.querySelector ? root : document;
    forEachMatch(scope, '[data-section-type="product-recommendations"]', function (el) {
      observeProductRecommendations(el);
    });
  }

  function boot(root) {
    initFeaturedCollectionTabsOnce();
    var scope = root && root.querySelector ? root : document;
    initProductMedia(scope);
    initProductRecommendationsSections(scope);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    initProductMediaRuntime();
    boot(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.classList && target.classList.contains('shopify-section')) {
      boot(target);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    releaseProductMedia(target);
    modalMediaObservers.forEach(function (state, modal) {
      if (state.section === target || target.contains(modal)) {
        state.observer.disconnect();
        modalMediaObservers.delete(modal);
        releaseProductMedia(modal);
      }
    });
    forEachMatch(target, '[data-section-type="product-recommendations"]', function (section) {
      if (recommendationObserver) recommendationObserver.unobserve(section);
      observedRecommendationSections.delete(section);
    });
  });

  document.addEventListener('dialog:before-show', function (event) {
    var modal = event.target;
    if (!productRoot || !modal.matches || !modal.matches('quick-buy-modal')) return;
    initProductMedia(modal);
    if (!modalMediaObservers.has(modal)) {
      modalMediaObservers.set(modal, {
        observer: observeProductMedia(modal),
        section: modal.closest('.shopify-section'),
      });
    }
  }, true);

  document.addEventListener('dialog:after-hide', function (event) {
    var modal = event.target;
    var state = modalMediaObservers.get(modal);
    if (!state) return;
    state.observer.disconnect();
    modalMediaObservers.delete(modal);
    releaseProductMedia(modal);
  }, true);
})();
